<script setup>
import { computed, inject } from 'vue';
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { SUPPORTED_CHANNELS } from '@/config/channels.js';
import { BRAND_LOGO_SRC } from '@/config/brand.js';
import LocaleSwitcher from '@/components/layout/LocaleSwitcher.vue';
import ChannelIcon from '@/components/common/ChannelIcon.vue';
import MaterialIcon from '@/components/common/MaterialIcon.vue';
import { PixelBlast } from '@/components/vuebits';

const { t } = useI18n();
const openContact = inject('openContact', () => {});
const year = new Date().getFullYear();
const appUrl = import.meta.env.VITE_APP_URL || 'http://localhost:5176';

const productLinks = computed(() => [
  { label: t('nav.product'), to: '/#product' },
  { label: t('nav.flywheel'), to: '/#flywheel' },
  { label: t('nav.calculator'), to: '/#calculator' },
  { label: t('nav.faq'), to: '/#faq' },
]);

const solutionLinks = computed(() => [
  { label: t('scenarios.1Title'), to: '/#scenarios' },
  { label: t('scenarios.2Title'), to: '/#scenarios' },
  { label: t('scenarios.3Title'), to: '/#scenarios' },
  { label: t('scenarios.4Title'), to: '/#scenarios' },
]);

const contactLinks = computed(() => [
  { label: t('nav.contact'), action: 'contact' },
  { label: t('nav.login'), href: appUrl, external: true },
]);

function onContactLinkClick(link) {
  if (link.action === 'contact') openContact();
}
</script>

<template>
  <footer id="site-footer" class="relative isolate bg-[#07111f] text-white">
    <!-- Pixel Blast 背景（需接收鼠标事件以触发 liquid 效果） -->
    <div class="absolute inset-0 z-0 overflow-hidden opacity-35">
      <PixelBlast
        class="size-full"
        variant="square"
        color="#60a5fa"
        :pixel-size="3"
        :pattern-scale="1.8"
        :pattern-density="1.05"
        :speed="0.45"
        :edge-fade="0.08"
        :enable-ripples="true"
        :ripple-intensity-scale="1.5"
        :ripple-thickness="0.08"
        :ripple-speed="0.45"
        :transparent="true"
        :liquid="true"
        :liquid-strength="0.14"
        :liquid-radius="1.3"
        :liquid-wobble-speed="5"
        :auto-pause-offscreen="true"
      />
    </div>
    <!-- 轻遮罩，不拦截鼠标 -->
    <div
      class="pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(circle_at_50%_15%,rgba(21,94,239,0.14),transparent_18%),linear-gradient(to_bottom,rgba(7,17,31,0.15),rgba(7,17,31,0.45))]"
      aria-hidden="true"
    />

    <div class="relative z-10 pointer-events-none [&_a]:pointer-events-auto [&_button]:pointer-events-auto">
    <!-- CTA 区块 -->
    <section class="section-shell py-16 sm:py-20" v-if="false">
      <div class="relative overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.06] px-6 py-12 text-center shadow-2xl sm:px-12">
        <div class="pointer-events-none absolute left-1/2 top-0 h-64 w-2/3 -translate-x-1/2 rounded-full bg-primary/30 blur-3xl" />
        <div class="relative">
          <p class="text-xs font-bold tracking-[0.18em] text-blue-300">{{ t('cta.eyebrow') }}</p>
          <h2 class="mx-auto mt-4 max-w-2xl text-3xl font-bold tracking-tight sm:text-5xl">
            {{ t('cta.title') }}
          </h2>
          <p class="mx-auto mt-4 max-w-xl text-sm leading-6 text-slate-300 sm:text-base">
            {{ t('cta.desc') }}
          </p>
          <button
            type="button"
            class="mt-8 rounded-xl bg-white px-8 py-3.5 text-base font-bold text-slate-950 shadow-xl transition hover:-translate-y-0.5 hover:bg-blue-50"
            @click="openContact()"
          >
            {{ t('cta.button') }}
          </button>
        </div>
      </div>
    </section>

    <!-- 主内容区 -->
    <div class="border-t border-white/10">
      <div class="section-shell py-10 sm:py-16">
        <div class="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1.6fr)] lg:gap-16">
          <!-- 品牌区 -->
          <div>
            <RouterLink to="/" class="inline-flex items-center gap-3">
              <span class="grid size-10 shrink-0 place-items-center overflow-hidden rounded-xl shadow-lg shadow-primary/25">
                <img :src="BRAND_LOGO_SRC" :alt="t('brand.name')" class="size-full object-contain" />
              </span>
              <div>
                <p class="text-base font-bold">{{ t('brand.name') }}</p>
                <p class="mt-0.5 text-[10px] font-semibold tracking-[0.14em] text-slate-400">{{ t('brand.tagline') }}</p>
              </div>
            </RouterLink>

            <p class="mt-5 max-w-sm text-sm leading-7 text-slate-400 sm:mt-6">
              {{ t('footer.desc') }}
            </p>

            <button
              type="button"
              class="mt-5 inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/[0.06] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10 sm:mt-6"
              @click="openContact()"
            >
              <MaterialIcon name="mail" class="size-4 text-blue-300" />
              {{ t('footer.contactCta') }}
            </button>

            <!-- 支持渠道 -->
            <div class="mt-7 sm:mt-8">
              <p class="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">{{ t('footer.channels') }}</p>
              <div class="mt-3 flex flex-wrap gap-2">
                <span
                  v-for="channel in SUPPORTED_CHANNELS"
                  :key="channel.id"
                  class="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-1.5 text-xs font-medium text-slate-300"
                >
                  <ChannelIcon :channel="channel" size-class="size-4" />
                  {{ channel.name }}
                </span>
              </div>
            </div>
          </div>

          <!-- 链接栏：手机两列填满宽度，避免右侧留白 -->
          <div class="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-3 sm:gap-10">
            <div>
              <p class="text-xs font-bold uppercase tracking-[0.16em] text-slate-300">{{ t('footer.productTitle') }}</p>
              <ul class="mt-4 space-y-2.5 sm:mt-5 sm:space-y-3">
                <li v-for="link in productLinks" :key="link.to">
                  <RouterLink
                    :to="link.to"
                    class="text-sm text-slate-400 transition-colors hover:text-white"
                  >
                    {{ link.label }}
                  </RouterLink>
                </li>
              </ul>
            </div>

            <div>
              <p class="text-xs font-bold uppercase tracking-[0.16em] text-slate-300">{{ t('footer.solutionsTitle') }}</p>
              <ul class="mt-4 space-y-2.5 sm:mt-5 sm:space-y-3">
                <li v-for="link in solutionLinks" :key="link.label">
                  <RouterLink
                    :to="link.to"
                    class="text-sm text-slate-400 transition-colors hover:text-white"
                  >
                    {{ link.label }}
                  </RouterLink>
                </li>
              </ul>
            </div>

            <div class="col-span-2 sm:col-span-1">
              <p class="text-xs font-bold uppercase tracking-[0.16em] text-slate-300">{{ t('footer.contactTitle') }}</p>
              <ul class="mt-4 flex flex-wrap gap-x-6 gap-y-2.5 sm:mt-5 sm:block sm:space-y-3 sm:gap-0">
                <li v-for="link in contactLinks" :key="link.label">
                  <button
                    v-if="link.action"
                    type="button"
                    class="text-sm text-slate-400 transition-colors hover:text-white"
                    @click="onContactLinkClick(link)"
                  >
                    {{ link.label }}
                  </button>
                  <a
                    v-else
                    :href="link.href"
                    :target="link.external ? '_blank' : undefined"
                    :rel="link.external ? 'noopener noreferrer' : undefined"
                    class="text-sm text-slate-400 transition-colors hover:text-white"
                  >
                    {{ link.label }}
                  </a>
                </li>
              </ul>

              <div class="mt-6 rounded-xl border border-white/10 bg-white/[0.04] p-3.5 sm:mt-8 sm:rounded-2xl sm:p-4">
                <p class="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{{ t('footer.supportHint') }}</p>
                <p class="mt-2 text-sm font-semibold text-white">{{ t('footer.supportValue') }}</p>
                <p class="mt-1 text-xs leading-5 text-slate-400">{{ t('footer.supportDesc') }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部栏：更透，让背景动效透出 -->
    <div class="border-t border-white/10 bg-[#050d18]/20">
      <div class="section-shell flex flex-col gap-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:gap-5 sm:py-6">
        <p class="text-xs text-slate-500">
          © {{ year }} {{ t('brand.name') }}. {{ t('footer.allRights') }}
        </p>

        <div class="flex flex-wrap items-center gap-4 sm:gap-6">
          <div class="flex items-center gap-4 text-xs text-slate-500">
            <RouterLink to="/legal/privacy" class="transition-colors hover:text-slate-300">{{ t('footer.privacy') }}</RouterLink>
            <span class="size-1 rounded-full bg-slate-700" aria-hidden="true" />
            <RouterLink to="/legal/terms" class="transition-colors hover:text-slate-300">{{ t('footer.terms') }}</RouterLink>
          </div>

          <LocaleSwitcher variant="dark" placement="top" />
        </div>
      </div>
    </div>
    </div>
  </footer>
</template>
